import type { SegmentPerson } from "#shared/segmentsTypes.ts";
import { Button } from "@/components/ui/button.tsx";
import { Card, CardContent } from "@/components/ui/card.tsx";
import { Note } from "@/components/ui/note.tsx";
import { Separator } from "@/components/ui/separator.tsx";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { useI18nextInstance } from "@squide/i18next";
import { PersonAvatar } from "@packages/core-client/people/PersonAvatar.tsx";
import { useCallback, useId } from "react";
import { useTranslation } from "react-i18next";
import { segmentsI18NextInstanceKey } from "../../i18n/i18next.ts";
import { MemberSearchField } from "./MemberSearchField.tsx";

interface MemberRowProps {
    person: SegmentPerson;
    isReadOnly?: boolean;
    onRemove: (personId: string) => void;
}

function MemberRow({ person, isReadOnly, onRemove }: MemberRowProps) {
    const i18nextInstance = useI18nextInstance(segmentsI18NextInstanceKey);
    const { t } = useTranslation("segmentMembers", { i18n: i18nextInstance });

    const handleRemove = useCallback(() => onRemove(person.id), [onRemove, person.id]);

    return (
        <li className="flex items-center gap-3 px-(--gutter) py-3">
            <PersonAvatar person={person} size="md" />
            <span className="flex min-w-0 flex-1 flex-col">
                <span className="text-fg truncate text-sm font-medium">{person.displayName}</span>
                {person.jobTitle && <span className="text-muted-fg truncate text-xs">{person.jobTitle}</span>}
            </span>
            {!isReadOnly && (
                <Button type="button" intent="plain" size="sq-sm" aria-label={t("remove", { name: person.displayName })} onPress={handleRemove}>
                    <XMarkIcon aria-hidden="true" />
                </Button>
            )}
        </li>
    );
}

export interface SegmentMembersProps {
    members: SegmentPerson[];
    isReadOnly?: boolean;
    shouldShowErrors?: boolean;
    onChange: (members: SegmentPerson[]) => void;
}

export function SegmentMembers({ members, isReadOnly, shouldShowErrors, onChange }: SegmentMembersProps) {
    const i18nextInstance = useI18nextInstance(segmentsI18NextInstanceKey);
    const { t } = useTranslation("segmentMembers", { i18n: i18nextInstance });
    const headingId = useId();

    const handleAdd = useCallback((person: SegmentPerson) => onChange([...members, person].toSorted((a, b) => a.displayName.localeCompare(b.displayName))), [members, onChange]);
    const handleRemove = useCallback((personId: string) => onChange(members.filter(member => member.id !== personId)), [members, onChange]);

    const isEmpty = members.length === 0;
    const hasError = shouldShowErrors && isEmpty && !isReadOnly;

    return (
        <section aria-labelledby={headingId} className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
                <p id={headingId} className="text-fg text-sm font-medium">
                    {t("heading")}
                </p>
                {!isEmpty && <span className="text-muted-fg text-sm">{t("count", { count: members.length })}</span>}
            </div>

            <Card className="[--gutter:--spacing(4)]">
                <CardContent className="flex flex-col px-0">
                    {!isReadOnly && (
                        <>
                            <div className="px-(--gutter) pb-4">
                                <MemberSearchField addedIds={members.map(member => member.id)} onAdd={handleAdd} />
                            </div>
                            <Separator />
                        </>
                    )}

                    {isEmpty ? (
                        <p className="text-muted-fg px-(--gutter) py-8 text-center text-sm">{t("empty")}</p>
                    ) : (
                        <ul className="divide-border flex flex-col divide-y">
                            {members.map(member => (
                                <MemberRow key={member.id} person={member} isReadOnly={isReadOnly} onRemove={handleRemove} />
                            ))}
                        </ul>
                    )}
                </CardContent>
            </Card>

            {hasError && (
                <Note intent="danger" role="alert">
                    {t("required")}
                </Note>
            )}
        </section>
    );
}
